/** One template's answers as a chart - or, for the same numbers as text, a
 *  table (the chart's twin). One chart language for every answer type
 *  (utils/chartModel, utils/useChartColors):
 *  - Yes / No: one two-part bar, Yes and No in two colours a colour-blind
 *    reader tells apart, with the counts beside it (never a two-slice donut);
 *  - one category / short text: horizontal bars, largest first, each
 *    category in its own colour (item 70: a category keeps its colour in
 *    every chart and range - chartModel.categoryColours over the server's
 *    `category_order`); past the shown ones the rest are one grey "Other" bar;
 *  - several categories: the same bars, the share counted per conversation -
 *    said above the bars, so three bars at 100% on one conversation read as
 *    what they are; the rest are named, never summed;
 *  - score / number: columns on a continuous axis - a score's whole range,
 *    an empty value a zero, many values in even ranges - shaded light to
 *    dark from low to high, the average marked.
 *  Every bar names its value; hovering it shows the full name, the count and
 *  the share, and a screen reader reads the same. A bar is not a control: no
 *  chart opens a list of its conversations (item 78). In Hebrew bars grow from
 *  the right and columns run from the right, numbers kept left-to-right. */
import { useMemo, useState, type ReactElement } from 'react';
import { Tooltip } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import type { LabelAggregation, ResponseType } from '../../types/insights';
import {
  byCount, categoryColours, formatCount, formatDecimal, formatShare, numericBuckets, rankCategories, takeaway,
  type Bucket, type Entry, type ScaleRange,
} from '../../utils/chartModel';
import { useChartColors } from '../../utils/useChartColors';
import { valueLabel } from './templateParts';

type ChartSize = 'compact' | 'normal' | 'large';

/** Bars before the rest fold into Other (one category) or are named (several). */
const BAR_LIMIT: Record<ChartSize, number> = { compact: 5, normal: 8, large: 12 };
const TEXT_LIMIT: Record<ChartSize, number> = { compact: 5, normal: 10, large: 15 };
const COLUMN_HEIGHT: Record<ChartSize, number> = { compact: 112, normal: 180, large: 260 };
const OTHER_NAMES_SHOWN = 6;
/** "Show the rest" on the template's page draws at most this many bars; past
 *  it the rest stay one Other bar (the Table pages through every answer). */
const EXPANDED_LIMIT = 50;

interface Props {
  type: ResponseType;
  aggregation: LabelAggregation;
  view: 'chart' | 'table';
  /** 'compact' on a dashboard card (a fixed height: the head of the list),
      'large' when the results pane is expanded. */
  size?: ChartSize;
  /** A score's scale (its whole range is shown). */
  range?: ScaleRange;
  /** A compact chart's way to everything it leaves out (the template's page). */
  onMore?: () => void;
}

/** Counts and shares as this chart prints them, in the reader's locale. */
function useNumbers(answered: number) {
  const { t } = useLanguage();
  const locale = activeLocale();
  return {
    count: (n: number) => formatCount(n, locale),
    share: (n: number) => formatShare(n, answered, locale),
    conversations: (n: number) => t('insights.tpl.chart.conversations', { count: n, n: formatCount(n, locale) }),
  };
}

/** A mark's tooltip (on hover; built only when shown) and the same words for
 *  a screen reader. */
function ChartTip({ name, count, share, note, children }: {
  name: string; count: string; share: string; note?: string;
  children: (label: string) => ReactElement;
}) {
  return (
    <Tooltip mouseEnterDelay={0.15} title={() => (
      <span className="tpl-tipline">
        <span className="tpl-tip__name" dir="auto">{name}</span>
        <bdi className="tpl-tip__num">{count} · {share}</bdi>
        {note && <span className="tpl-tipline__note">{note}</span>}
      </span>
    )}>
      {children(`${name}: ${count}, ${share}`)}
    </Tooltip>
  );
}

export function TemplateChart({ type, aggregation, view, size = 'normal', range, onMore }: Props) {
  const answered = aggregation.total || 0;
  const distribution = aggregation.distribution || {};
  if (view === 'table') return <AnswerTable type={type} distribution={distribution} answered={answered} />;
  if (type === 'boolean') return <YesNo distribution={distribution} answered={answered} size={size} />;
  if (type === 'scale' || type === 'numeric') {
    return <Columns distribution={distribution} mean={aggregation.numeric_summary?.mean}
                    range={type === 'scale' ? range : undefined} answered={answered} size={size} />;
  }
  return <Bars type={type} distribution={distribution} answered={answered} size={size} onMore={onMore}
               order={aggregation.category_order} />;
}

/** The one line a template's results lead with: what most people answered. */
export function Takeaway({ type, aggregation, range }: { type: ResponseType; aggregation: LabelAggregation; range?: ScaleRange }) {
  const { t } = useLanguage();
  const locale = activeLocale();
  const take = takeaway(type, aggregation);
  if (!take) return null;
  if (take.kind === 'few') {
    return (
      <p className="tpl-take tpl-take--text tpl-take--muted" data-testid="template-takeaway-few">
        <span className="tpl-take__figure">{t('insights.tpl.take.tooFew')}</span>
        <span className="tpl-take__caption">
          {t('insights.tpl.take.tooFewCaption', { count: take.whole, n: formatCount(take.whole, locale),
                                                  min: formatCount(take.needed, locale) })}
        </span>
      </p>
    );
  }
  if (take.kind === 'tie') {
    const share = formatShare(take.count, take.whole, locale);
    return (
      <p className="tpl-take tpl-take--text tpl-take--muted" data-testid="template-takeaway-tie">
        <span className="tpl-take__figure">{t('insights.tpl.take.tie')}</span>
        <span className="tpl-take__caption">{t(type === 'multi_label'
          ? 'insights.tpl.take.tieInConversations' : 'insights.tpl.take.tieOfAnswers',
        { count: take.values.length, n: formatCount(take.values.length, locale), share })}</span>
      </p>
    );
  }
  if (take.kind === 'top') {
    const name = valueLabel(t, type, take.value);
    const share = formatShare(take.count, take.whole, locale);
    return (
      <p className="tpl-take tpl-take--text">
        <span className="tpl-take__figure"><span dir="auto" title={name}>{name}</span></span>
        <span className="tpl-take__caption">{type === 'multi_label'
          ? t('insights.tpl.take.inConversations', { share }) : t('insights.tpl.take.ofAnswers', { share })}</span>
      </p>
    );
  }
  const scale = type === 'scale' && range?.min != null && range?.max != null ? range : null;
  const [figure, caption] = take.kind === 'share'
    ? [formatShare(take.yes, take.whole, locale),
      t('insights.tpl.take.saidYes', { n: formatCount(take.yes, locale), whole: formatCount(take.whole, locale) })]
    : [formatDecimal(take.mean, locale), scale
      ? t('insights.tpl.take.averageOf', { min: formatDecimal(scale.min as number, locale), max: formatDecimal(scale.max as number, locale) })
      : t('insights.tpl.take.average', { median: take.median === null ? '—' : formatDecimal(take.median, locale) })];
  return (
    <p className="tpl-take">
      <span className="tpl-take__figure"><bdi>{figure}</bdi></span>
      <span className="tpl-take__caption">{caption}</span>
    </p>
  );
}

function Bars({ type, distribution, answered, size, onMore, order }: {
  type: ResponseType; distribution: Record<string, number>; answered: number; size: ChartSize;
  onMore?: () => void;
  /** The template's lasting category order (each category's colour). */
  order?: string[];
}) {
  const { t } = useLanguage();
  const colors = useChartColors();
  const num = useNumbers(answered);
  const [all, setAll] = useState(false);
  const several = type === 'multi_label';
  // As many bars as the palette has colours: two bars of one chart never share one.
  const limit = Math.min((type === 'free_text' ? TEXT_LIMIT : BAR_LIMIT)[size], colors.categorical.length);
  const ranked = useMemo(() => rankCategories(distribution, all ? EXPANDED_LIMIT : limit, !several),
    [distribution, all, limit, several]);
  const max = Math.max(1, ...ranked.shown.map((e) => e.count), ranked.other?.count || 0);
  // The rest: on a card, the template's page; on the page, up to EXPANDED_LIMIT here.
  const showRest = size === 'compact' || all ? onMore : () => setAll(true);
  const shareNote = several ? t('insights.tpl.chart.ofConversations') : t('insights.tpl.chart.ofAnswers');
  // Short answers have no lasting identity: they take the palette in the order shown.
  const fills = useMemo(() => categoryColours(ranked.shown.map((e) => e.value),
    type === 'free_text' ? undefined : order, colors.categorical), [ranked.shown, order, type, colors.categorical]);

  const bar = (key: string, e: Entry, name: string, note: string, fill: string) => (
    <ChartTip key={key} name={name} count={num.conversations(e.count)} share={num.share(e.count)} note={note}>
      {(label) => (
        <span role="listitem" className={`tpl-bar${key === '__other__' ? ' tpl-bar--other' : ''}`} aria-label={label}>
          <span className="tpl-bar__label" dir="auto">{name}</span>
          <span className="tpl-bar__plot" aria-hidden="true">
            <span className="tpl-bar__fill"
                  style={{ inlineSize: `calc((100% - var(--tpl-bar-value)) * ${e.count / max})`, background: fill }} />
            <span className="tpl-bar__value"><bdi>{num.count(e.count)}</bdi>
              <span className="tpl-bar__share"> · <bdi>{num.share(e.count)}</bdi></span></span>
          </span>
        </span>
      )}
    </ChartTip>
  );

  let otherBar = null;
  if (ranked.other) {
    const { members, count } = ranked.other;
    const name = t(type === 'free_text' ? 'insights.tpl.chart.otherAnswers' : 'insights.tpl.chart.otherCategories',
      { n: num.count(members.length) });
    const names = members.slice(0, OTHER_NAMES_SHOWN).map((m) => valueLabel(t, type, m.value)).join(', ');
    const more = members.length > OTHER_NAMES_SHOWN
      ? ` ${t('insights.tpl.chart.andMore', { n: num.count(members.length - OTHER_NAMES_SHOWN) })}` : '';
    otherBar = bar('__other__', { value: '', count }, name, names + more, colors.other);
  }

  return (
    <div className={`tpl-bars tpl-bars--${size}`}>
      {several && (
        // A card has room for one line: the short note, the whole one on hover.
        <p className={`tpl-bars__note${size === 'compact' ? ' tpl-bars__note--short' : ''}`} data-testid="template-share-note"
           title={size === 'compact' ? t('insights.tpl.chart.multiShareNote') : undefined}>
          {t(size === 'compact' ? 'insights.tpl.chart.multiShareNoteShort' : 'insights.tpl.chart.multiShareNote')}
        </p>
      )}
      <div role="list" aria-label={t('insights.tpl.chart.alt')}>
        {ranked.shown.map((e) => bar(e.value, e, valueLabel(t, type, e.value), shareNote, fills.get(e.value) || colors.lead))}
        {otherBar}
      </div>
      {(ranked.hidden > 0 || all) && (
        <button type="button" className="tpl-link tpl-bars__more" onClick={all ? () => setAll(false) : showRest}>
          {all ? t('insights.tpl.chart.showTop', { count: limit })
            : t('insights.tpl.chart.moreCategories', { n: num.count(ranked.hidden) })}
        </button>
      )}
    </div>
  );
}

function YesNo({ distribution, answered, size }: {
  distribution: Record<string, number>; answered: number; size: ChartSize;
}) {
  const { t } = useLanguage();
  const colors = useChartColors();
  const num = useNumbers(answered);
  const parts = [
    { value: 'true', count: distribution.true || 0, color: colors.categorical[0] },
    { value: 'false', count: distribution.false || 0, color: colors.categorical[1] },
  ].map((p) => ({ ...p, name: valueLabel(t, 'boolean', p.value), share: num.share(p.count) }));
  return (
    <div className={`tpl-yn tpl-yn--${size}`}>
      <div className="tpl-yn__bar" role="img"
           aria-label={t('insights.tpl.chart.yesNoAlt', { yes: parts[0].share, no: parts[1].share })}>
        {parts.filter((p) => p.count > 0).map((p) => (
          <ChartTip key={p.value} name={p.name} count={num.conversations(p.count)} share={p.share}>
            {() => <span className="tpl-yn__part" aria-hidden="true" style={{ flexGrow: p.count, background: p.color }} />}
          </ChartTip>
        ))}
      </div>
      <div className="tpl-yn__legend" role="list">
        {parts.map((p) => (
          <span key={p.value} role="listitem" className="tpl-legend"
                aria-label={`${p.name}: ${num.conversations(p.count)}, ${p.share}`}>
            <span className="tpl-swatch" style={{ background: p.color }} aria-hidden="true" />
            <span className="tpl-legend__name" dir="auto">{p.name}</span>
            <span className="tpl-legend__count">
              <bdi>{p.share}</bdi><span className="tpl-legend__n"> · <bdi>{num.count(p.count)}</bdi></span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Columns({ distribution, mean, range, answered, size }: {
  distribution: Record<string, number>; mean?: number; range?: ScaleRange; answered: number; size: ChartSize;
}) {
  const { t } = useLanguage();
  const colors = useChartColors();
  const num = useNumbers(answered);
  const buckets = useMemo(() => numericBuckets(distribution, range),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [distribution, range?.min, range?.max]);
  const ramp = useMemo(() => colors.ramp(buckets.length), [colors, buckets.length]);
  if (!buckets.length) return null;
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const n = buckets.length;
  const every = n <= 12 ? 1 : Math.ceil(n / (size === 'compact' ? 6 : 12));
  const labelled = n <= (size === 'compact' ? 12 : 16);
  const meanAt = mean === undefined ? null : position(buckets, mean);
  return (
    <div className={`tpl-cols tpl-cols--${size}`} style={{ ['--tpl-cols-h' as string]: `${COLUMN_HEIGHT[size]}px` }}>
      <div className="tpl-cols__plot" role="list" aria-label={t('insights.tpl.chart.alt')}>
        {buckets.map((b, i) => (
          <ChartTip key={b.label} name={b.label} count={num.conversations(b.count)} share={num.share(b.count)}>
            {(label) => (
              <span role="listitem" className="tpl-col" aria-label={label}>
                <span className={`tpl-col__count${b.count ? '' : ' is-zero'}`}>
                  {labelled && <bdi>{num.count(b.count)}</bdi>}
                </span>
                <span className="tpl-col__bar" aria-hidden="true"
                      style={{ blockSize: b.count ? `${(b.count / max) * 100}%` : 0, background: ramp[i] }} />
                <span className="tpl-col__tick">{i % every === 0 || i === n - 1 ? <bdi dir="ltr">{b.label}</bdi> : ''}</span>
              </span>
            )}
          </ChartTip>
        ))}
        {meanAt !== null && (
          <span className="tpl-cols__mean" style={{ insetInlineStart: `${meanAt * 100}%` }} aria-hidden="true">
            <span className="tpl-cols__mean-label">
              {t('insights.tpl.chart.avgShort', { value: formatDecimal(mean as number, activeLocale()) })}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

/** Where a value falls along the columns (0 = start edge, 1 = end edge):
 *  between the centres of the two columns around it. */
function position(buckets: Bucket[], value: number): number {
  const n = buckets.length;
  const centres = buckets.map((b) => (b.lo + b.hi) / 2);
  if (value <= centres[0]) return 0.5 / n;
  for (let i = 1; i < n; i += 1) {
    if (value <= centres[i]) {
      return (i - 0.5 + (value - centres[i - 1]) / (centres[i] - centres[i - 1])) / n;
    }
  }
  return (n - 0.5) / n;
}

function AnswerTable({ type, distribution, answered }: {
  type: ResponseType; distribution: Record<string, number>; answered: number;
}) {
  const { t } = useLanguage();
  const num = useNumbers(answered);
  const entries = useMemo(() => {
    const list = byCount(distribution);
    if (type === 'scale' || type === 'numeric') return list.sort((a, b) => Number(a.value) - Number(b.value));
    if (type === 'boolean') return list.sort((a, b) => Number(b.value === 'true') - Number(a.value === 'true'));
    return list;
  }, [distribution, type]);
  const [all, setAll] = useState(false);
  const limit = 50;
  const shown = all ? entries : entries.slice(0, limit);
  return (
    <div className="tpl-table-wrap">
      <table className="tpl-table">
        <caption className="tpl-sr">{t('insights.tpl.chart.tableCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('insights.tpl.chart.colAnswer')}</th>
            <th scope="col" className="tpl-table__num">{t('insights.tpl.chart.colConversations')}</th>
            <th scope="col" className="tpl-table__num">
              {type === 'multi_label' ? t('insights.tpl.chart.colShareConversations') : t('insights.tpl.chart.colShare')}
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((e) => (
            <tr key={e.value}>
              <td className="tpl-table__answer"><span dir="auto">{valueLabel(t, type, e.value)}</span></td>
              <td className="tpl-table__num"><bdi>{num.count(e.count)}</bdi></td>
              <td className="tpl-table__num"><bdi>{num.share(e.count)}</bdi></td>
            </tr>
          ))}
        </tbody>
      </table>
      {entries.length > limit && (
        <button type="button" className="tpl-link" onClick={() => setAll((v) => !v)}>
          {all ? t('insights.tpl.chart.showTop', { count: limit }) : t('insights.tpl.chart.showAll', { count: entries.length })}
        </button>
      )}
      {type === 'multi_label' && <p className="tpl-chart__note">{t('insights.tpl.chart.multiNote')}</p>}
    </div>
  );
}
